import { Box, Stack } from '@mantine/core';
import type { UserRole } from '@repo/common/enums/UserRole';
import { Icon } from '@repo/foundations';
import { useMemo } from 'react';
import { useLocation, useParams } from 'react-router';
import {
	useAuthUser,
	useCurrentUser,
	useSuspenseFeatureAccess,
} from '../../api';
import { useFeatureFlags } from '../../utils/featureFlags';
import { evaluateFeatureCondition } from '../../utils/featureFlags/complexFeatureUtils';
import { SideBarCategoryToggle } from '../SecodaAppShell/SideBar/SideBarCategoryToggle';
import SideBarLink from '../SecodaAppShell/SideBar/SideBarLink';
import type { TabType } from './settingsUtils';
import { featureTabs, profileTabs, workspaceTabs } from './settingsUtils';

export function SettingsSideBarLinks() {
	const location = useLocation();
	const { subtab } = useParams();
	const { workspace } = useAuthUser();
	const { user } = useAuthUser();
	const featureFlags = useFeatureFlags();
	const featureAccess = useSuspenseFeatureAccess();
	const { data: currentUser } = useCurrentUser();
	const userPermissions = currentUser?.permissions;

	const filteredTabs = useMemo(() => {
		function filterTabs(tabs: TabType[]) {
			return tabs.filter((tab) => {
				// Check feature condition
				if (
					tab.featureCondition &&
					!evaluateFeatureCondition(
						tab.featureCondition,
						featureFlags,
						featureAccess
					)
				) {
					return false;
				}

				if (workspace.migrated_permission_v2) {
					if (tab.v2AllowedPermissions) {
						if (
							tab.v2DeniedPermissions &&
							tab.v2DeniedPermissions.some((permission) =>
								userPermissions?.includes(permission)
							)
						) {
							return false;
						}

						return tab.v2AllowedPermissions.some((permission) =>
							userPermissions?.includes(permission)
						);
					}
				} else {
					if (tab.allowedRoles) {
						return user
							? tab.allowedRoles.includes(user.role as UserRole)
							: false;
					}
				}

				return true;
			});
		}
		return {
			profileTabs: filterTabs(profileTabs(subtab)),
			workspaceTabs: filterTabs(workspaceTabs(workspace)),
			featureTabs: filterTabs(featureTabs()),
		};
	}, [featureFlags, featureAccess, user, userPermissions, workspace, subtab]);

	const isBeta = (tab: TabType) => {
		if (tab.isBeta && tab.betaExpiresAt) {
			return new Date(tab.betaExpiresAt) > new Date();
		}
		return false;
	};

	return (
		<Stack spacing={'md'}>
			<SideBarCategoryToggle
				label="Account"
				toggleKey="settings-account-sidebar-collapsable-opened"
				forceOpen={true}
			>
				<Stack spacing={0} align="flex-start">
					{filteredTabs.profileTabs.map((tab) => (
						<Box key={tab.id} pb="5xs" w="100%">
							<SideBarLink
								link={{
									id: `nav::settings::${tab.id}`,
									label: tab.sidebarTitle ?? tab.name,
									icon: (
										<Icon name={tab.iconName} color="icon/primary/default" />
									),
									href: [`/settings/${tab.id}`],
									matchExact: true,
									show: true,
								}}
								selected={location.pathname === `/settings/${tab.id}`}
							/>
						</Box>
					))}
				</Stack>
			</SideBarCategoryToggle>

			{filteredTabs.featureTabs.length > 0 && (
				<SideBarCategoryToggle
					label="Features"
					toggleKey="settings-features-sidebar-collapsable-opened"
					forceOpen={true}
				>
					<Stack spacing={0} align="flex-start">
						{filteredTabs.featureTabs.map((tab) => (
							<Box key={tab.id} pb="5xs" w="100%">
								<SideBarLink
									link={{
										id: `nav::settings::${tab.id}`,
										label: isBeta(tab)
											? `${tab.sidebarTitle ?? tab.name} (Beta)`
											: (tab.sidebarTitle ?? tab.name),
										icon: (
											<Icon name={tab.iconName} color="icon/primary/default" />
										),
										href: [`/settings/${tab.id}`],
										matchExact: true,
										show: true,
									}}
									selected={location.pathname === `/settings/${tab.id}`}
								/>
							</Box>
						))}
					</Stack>
				</SideBarCategoryToggle>
			)}

			<SideBarCategoryToggle
				label="Administration"
				toggleKey="settings-administration-sidebar-collapsable-opened"
				forceOpen={true}
			>
				<Stack spacing={0} align="flex-start">
					{filteredTabs.workspaceTabs.map((tab) => (
						<Box key={tab.id} pb="5xs" w="100%">
							<SideBarLink
								link={{
									id: `nav::settings::${tab.id}`,
									label: isBeta(tab)
										? `${tab.sidebarTitle ?? tab.name} (Beta)`
										: (tab.sidebarTitle ?? tab.name),
									icon: (
										<Icon name={tab.iconName} color="icon/primary/default" />
									),
									href: [`/settings/${tab.id}`],
									matchExact: true,
									show: true,
								}}
								selected={location.pathname === `/settings/${tab.id}`}
							/>
						</Box>
					))}
				</Stack>
			</SideBarCategoryToggle>
		</Stack>
	);
}
